import React, { useEffect, useRef } from 'react';
import Plotly from 'plotly.js';
import { plotlyResizeIfDisplayed } from '../utils/plotlySafeResize';
import './JackSyncedPlotlyCharts.css';

/** Match suspension panel: front blue, rear pink. */
const TRACE_FRONT = '#42a5f5';
const TRACE_REAR = '#ec4899';

/** Match jack-benchmark.html: regular scatter (not scattergl). */
const TRACE_TYPE = 'scatter';

const isMobile =
  typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const plotlyConfig = {
  responsive: true,
  doubleClick: false,
  scrollZoom: !isMobile,
  displayModeBar: !isMobile,
  modeBarButtonsToRemove: ['lasso2d', 'select2d', 'autoScale2d', 'resetScale2d'],
};

function axisStyle() {
  return {
    title: { font: { color: '#9aa0a6' } },
    gridcolor: 'rgba(255, 255, 255, 0.08)',
    zerolinecolor: 'rgba(255, 255, 255, 0.15)',
    color: '#888',
    linecolor: 'rgba(255, 255, 255, 0.2)',
  };
}

function buildPackingShapes(packingEvents, params) {
  const shapes = [];
  if (!packingEvents || !params) return shapes;
  const front = packingEvents.front || [];
  const rear = packingEvents.rear || [];
  const all = [
    ...front.map((e) => ({ ...e, _side: 'front' })),
    ...rear.map((e) => ({ ...e, _side: 'rear' })),
  ];
  all.forEach((event) => {
    const sev = Number(event.severity) || 0;
    const fillcolor =
      sev <= 25
        ? 'rgba(255,255,0,0.2)'
        : sev <= 50
          ? 'rgba(255,165,0,0.2)'
          : sev <= 75
            ? 'rgba(255,100,0,0.3)'
            : 'rgba(255,0,0,0.3)';
    shapes.push({
      type: 'rect',
      xref: 'x',
      yref: 'paper',
      x0: event.start_time,
      x1: event.end_time,
      y0: 0,
      y1: 1,
      fillcolor,
      opacity: 0.5,
      line: { width: 0 },
    });
  });
  const rearTravel = Number(params.rearTravel);
  const sagPct = Number(params.sagPercent);
  if (Number.isFinite(rearTravel) && Number.isFinite(sagPct)) {
    const rearBaseline = rearTravel * (sagPct / 100);
    shapes.push({
      type: 'line',
      xref: 'paper',
      yref: 'y',
      x0: 0,
      x1: 1,
      y0: rearBaseline,
      y1: rearBaseline,
      line: { color: TRACE_REAR, width: 1, dash: 'dash' },
    });
  }
  return shapes;
}

function buildAnalysisWindowShapes(analysisWindow) {
  if (!analysisWindow || typeof analysisWindow.start !== 'number' || !Number.isFinite(analysisWindow.start)) {
    return [];
  }
  const shapes = [
    {
      type: 'line',
      xref: 'x',
      yref: 'paper',
      x0: analysisWindow.start,
      x1: analysisWindow.start,
      y0: 0,
      y1: 1,
      line: { color: 'rgba(66, 165, 245, 0.75)', width: 2, dash: 'dot' },
      layer: 'above',
    },
  ];
  if (typeof analysisWindow.end === 'number' && Number.isFinite(analysisWindow.end)) {
    shapes.push({
      type: 'line',
      xref: 'x',
      yref: 'paper',
      x0: analysisWindow.end,
      x1: analysisWindow.end,
      y0: 0,
      y1: 1,
      line: { color: 'rgba(236, 72, 153, 0.75)', width: 2, dash: 'dot' },
      layer: 'above',
    });
  }
  return shapes;
}

function playheadShapes(t) {
  if (t == null || !Number.isFinite(t)) return [];
  return [
    {
      type: 'line',
      xref: 'x',
      yref: 'paper',
      x0: t,
      x1: t,
      y0: 0,
      y1: 1,
      line: { color: 'rgba(255, 255, 255, 0.9)', width: 2 },
      layer: 'above',
    },
  ];
}

const velocityZeroLine = {
  type: 'line',
  xref: 'paper',
  yref: 'y',
  x0: 0,
  x1: 1,
  y0: 0,
  y1: 0,
  line: { color: '#666', width: 1, dash: 'dot' },
};

/**
 * Two Plotly charts (position + velocity vs time), x-zoom linked — same behavior as public/jack-benchmark.html.
 */
export default function JackSyncedPlotlyCharts({
  processedData = [],
  analysisResults = null,
  currentTime = 0,
  dataOffset = 0,
  onSeek,
  /** Programmatic x zoom (data time, s); same idea as jack-benchmark.html zoomToEvent */
  xZoomRange = null,
  onXZoomRangeCleared,
  chartHeight = 280,
  /** Vertical markers at analysis trim window (data time, s) */
  analysisWindow = null,
}) {
  const posRef = useRef(null);
  const velRef = useRef(null);
  const posGdRef = useRef(null);
  const velGdRef = useRef(null);
  const syncingRef = useRef(false);
  const onSeekRef = useRef(onSeek);
  const dataOffsetRef = useRef(dataOffset);
  onSeekRef.current = onSeek;
  dataOffsetRef.current = dataOffset;

  const dataTime =
    currentTime != null && Number.isFinite(currentTime)
      ? Math.max(0, currentTime + (Number(dataOffset) || 0))
      : null;
  const dataTimeRef = useRef(dataTime);
  dataTimeRef.current = dataTime;

  useEffect(() => {
    const posEl = posRef.current;
    const velEl = velRef.current;
    if (!posEl || !velEl || !Array.isArray(processedData) || processedData.length === 0) {
      posGdRef.current = null;
      velGdRef.current = null;
      return undefined;
    }

    const params = analysisResults?.params || {};
    const packingEvents = analysisResults?.packingEvents || { front: [], rear: [] };
    const packShapes = buildPackingShapes(packingEvents, params);
    const trimShapes = buildAnalysisWindowShapes(analysisWindow);
    const ph = playheadShapes(dataTimeRef.current);

    const times = processedData.map((d) => d.time);
    const frontData = processedData.map((d) => d.front);
    const rearData = processedData.map((d) => d.rear);
    const frontVel = processedData.map((d) => d.front_vel);
    const rearVel = processedData.map((d) => d.rear_vel);

    const axis = axisStyle();
    const posTraces = [
      {
        x: times,
        y: frontData,
        name: 'Front (Filtered)',
        type: TRACE_TYPE,
        mode: 'lines',
        line: { color: TRACE_FRONT, width: 1.5 },
      },
      {
        x: times,
        y: rearData,
        name: 'Rear (Filtered)',
        type: TRACE_TYPE,
        mode: 'lines',
        line: { color: TRACE_REAR, width: 1.5 },
      },
    ];

    const posLayout = {
      paper_bgcolor: '#0a0a0a',
      plot_bgcolor: '#0a0a0a',
      font: { color: '#bbb' },
      margin: { t: 36, r: 20, b: 44, l: 56 },
      xaxis: { ...axis, title: { text: 'Time (s)', ...axis.title } },
      yaxis: { ...axis, title: { text: 'Position (mm)', ...axis.title } },
      shapes: [...packShapes, ...trimShapes, ...ph],
      legend: { orientation: 'h', y: 1.12, font: { color: '#ccc' } },
      hovermode: 'x unified',
    };

    const velTraces = [
      {
        x: times,
        y: frontVel,
        name: 'Front Vel (Filtered)',
        type: TRACE_TYPE,
        mode: 'lines',
        line: { color: TRACE_FRONT, width: 1.5 },
      },
      {
        x: times,
        y: rearVel,
        name: 'Rear Vel (Filtered)',
        type: TRACE_TYPE,
        mode: 'lines',
        line: { color: TRACE_REAR, width: 1.5 },
      },
    ];

    const velLayout = {
      paper_bgcolor: '#0a0a0a',
      plot_bgcolor: '#0a0a0a',
      font: { color: '#bbb' },
      margin: { t: 36, r: 20, b: 44, l: 56 },
      xaxis: { ...axis, title: { text: 'Time (s)', ...axis.title } },
      yaxis: { ...axis, title: { text: 'Velocity (mm/s)', ...axis.title } },
      shapes: [velocityZeroLine, ...trimShapes, ...ph],
      legend: { orientation: 'h', y: 1.12, font: { color: '#ccc' } },
      hovermode: 'x unified',
    };

    let cancelled = false;

    const onPosRelayout = (ev) => {
      const velGd = velGdRef.current;
      if (syncingRef.current || !velGd) return;
      syncingRef.current = true;
      const update = {};
      if (ev['xaxis.range[0]'] !== undefined) {
        update['xaxis.range'] = [ev['xaxis.range[0]'], ev['xaxis.range[1]']];
      }
      if (ev['xaxis.autorange']) update['xaxis.autorange'] = true;
      if (Object.keys(update).length > 0) {
        Plotly.relayout(velGd, update).catch(() => {});
      }
      syncingRef.current = false;
    };

    const onVelRelayout = (ev) => {
      const posGd = posGdRef.current;
      if (syncingRef.current || !posGd) return;
      syncingRef.current = true;
      const update = {};
      if (ev['xaxis.range[0]'] !== undefined) {
        update['xaxis.range'] = [ev['xaxis.range[0]'], ev['xaxis.range[1]']];
      }
      if (ev['xaxis.autorange']) update['xaxis.autorange'] = true;
      if (Object.keys(update).length > 0) {
        Plotly.relayout(posGd, update).catch(() => {});
      }
      syncingRef.current = false;
    };

    const onPlotClick = (ev) => {
      const seek = onSeekRef.current;
      if (!seek || !ev?.points?.length) return;
      const x = ev.points[0].x;
      if (x != null && Number.isFinite(x)) {
        seek(x - (Number(dataOffsetRef.current) || 0));
      }
    };

    (async () => {
      try {
        await Plotly.newPlot(posEl, posTraces, posLayout, plotlyConfig);
        await Plotly.newPlot(velEl, velTraces, velLayout, plotlyConfig);
        if (cancelled) return;
        posGdRef.current = posEl;
        velGdRef.current = velEl;
        posEl.on('plotly_relayout', onPosRelayout);
        velEl.on('plotly_relayout', onVelRelayout);
        posEl.on('plotly_click', onPlotClick);
        velEl.on('plotly_click', onPlotClick);
      } catch (e) {
        console.error('Jack Plotly charts failed:', e);
      }
    })();

    const onResize = () => {
      plotlyResizeIfDisplayed(posEl);
      plotlyResizeIfDisplayed(velEl);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
      Plotly.purge(posEl);
      Plotly.purge(velEl);
      posGdRef.current = null;
      velGdRef.current = null;
    };
  }, [processedData, analysisResults, analysisWindow]);

  useEffect(() => {
    const posGd = posGdRef.current;
    const velGd = velGdRef.current;
    if (!posGd || !velGd || !posGd.layout) return;

    const params = analysisResults?.params || {};
    const packingEvents = analysisResults?.packingEvents || { front: [], rear: [] };
    const packShapes = buildPackingShapes(packingEvents, params);
    const trimShapes = buildAnalysisWindowShapes(analysisWindow);
    const ph = playheadShapes(dataTime);

    Plotly.relayout(posGd, { shapes: [...packShapes, ...trimShapes, ...ph] }).catch(() => {});
    Plotly.relayout(velGd, { shapes: [velocityZeroLine, ...trimShapes, ...ph] }).catch(() => {});
  }, [dataTime, analysisResults, analysisWindow]);

  useEffect(() => {
    const posGd = posGdRef.current;
    const velGd = velGdRef.current;
    if (!posGd || !velGd) return;
    if (
      !xZoomRange ||
      !Number.isFinite(xZoomRange.min) ||
      !Number.isFinite(xZoomRange.max) ||
      xZoomRange.min >= xZoomRange.max
    ) {
      return;
    }
    const update = {
      'xaxis.range': [xZoomRange.min, xZoomRange.max],
      'xaxis.autorange': false,
    };
    Plotly.relayout(posGd, update).catch(() => {});
    Plotly.relayout(velGd, update).catch(() => {});
  }, [xZoomRange, processedData, analysisResults]);

  /** Clear fixed x/y zoom and return to autorange on both axes (Plotly bracket keys drop prior ranges). */
  const relayoutXYFullReset = (gd) =>
    Plotly.relayout(gd, {
      'xaxis.autorange': true,
      'xaxis.range[0]': null,
      'xaxis.range[1]': null,
      'yaxis.autorange': true,
      'yaxis.range[0]': null,
      'yaxis.range[1]': null,
    });

  const handleResetZoom = () => {
    onXZoomRangeCleared?.();
    const posGd = posGdRef.current;
    const velGd = velGdRef.current;
    const jobs = [];
    if (posGd) jobs.push(relayoutXYFullReset(posGd));
    if (velGd) jobs.push(relayoutXYFullReset(velGd));
    if (jobs.length === 0) return;
    // Relayout is async; linked plotly_relayout handlers can otherwise re-apply a stale x range
    // between the two charts while reset is in flight.
    syncingRef.current = true;
    Promise.all(jobs)
      .catch(() => {})
      .finally(() => {
        syncingRef.current = false;
      });
  };

  if (!Array.isArray(processedData) || processedData.length === 0) return null;

  return (
    <div className="jack-synced-plotly-wrap">
      <div className="jack-synced-plotly-toolbar">
        <button
          type="button"
          className="jack-synced-plotly-reset"
          onClick={handleResetZoom}
          title="Reset X and Y zoom on both charts to full data range"
        >
          Reset zoom
        </button>
      </div>
      <div className="jack-synced-plotly-chart" style={{ height: chartHeight }}>
        <div ref={posRef} className="jack-synced-plotly-div" />
      </div>
      <div className="jack-synced-plotly-chart" style={{ height: chartHeight }}>
        <div ref={velRef} className="jack-synced-plotly-div" />
      </div>
    </div>
  );
}
